import React from 'react';
import produce from 'immer';
import Checkbox from 'jsx/elements/inputs/Checkbox';
import Spinner from 'jsx/elements/spinner';
import Flex from 'jsx/layout/Flex';
import { usePortfolioTranslation as useLocale } from 'jsx/hooks/useTranslation';
import Warning from 'jsx/elements/messages/Warning';
import Button from 'jsx/elements/buttons/Button';

import { api } from 'api';
import Modal from 'components/Modal';
import { useMonitoring } from './MonitorProvider';
import './NotificationsModal.less';

export const NotificationsModal = ({ onClose }) => {
  const { selected } = useMonitoring();
  const { t, lang } = useLocale();
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const { data, submitStatus, status, initial } = state;
  const text = locale[lang];
  const rules = text.rules;

  React.useEffect(() => {
    load();
  }, []);

  const onSubmit = async () => {
    dispatch({ type: 'SUBMIT' });
    const res = await api.monitorRules.update(data);
    if (!res.error) {
      dispatch({ type: 'SUBMIT_SUCCESS' });
    } else {
      dispatch({ type: 'SUBMIT_ERROR' });
    }
  };

  const load = async () => {
    dispatch({ type: 'SET_LOADING' });
    const res = await api.monitorRules.load(selected.monitorId);
    if (!res.error) {
      dispatch({ type: 'SET_DATA', payload: res });
    }
  };

  const setOption = React.useCallback(
    (ruleID, optionID) => {
      dispatch({
        type: 'SET_OPTION',
        payload: { rule: Number(ruleID), option: Number(optionID) },
      });
    },
    [dispatch]
  );

  const toggleRule = React.useCallback(
    (ruleID) => {
      dispatch({ type: 'TOGGLE_RULE', payload: Number(ruleID) });
    },
    [dispatch]
  );

  const setAll = React.useCallback(() => {
    dispatch({ type: 'SET_ALL' });
  }, [dispatch]);

  const all = React.useMemo(() => data.every((rule) => !rule.active), [data]);
  const edited = React.useMemo(() => {
    return JSON.stringify(data) !== JSON.stringify(initial);
  }, [data, initial]);

  return (
    <Modal
      header={text.title}
      isOpen
      footer={
        edited ? (
          <>
            <Button text={t('modal_cancel')} color="clear" onClick={onClose} />
            <Button
              text={t('modal_confirm')}
              color="red"
              onClick={onSubmit}
              disabled={status === 'loading' || submitStatus === 'loading'}
            />
          </>
        ) : (
          <>
            <div />
            <Button text={text.close} color="gray" onClick={onClose} />
          </>
        )
      }
    >
      {submitStatus === 'loading' && (
        <div style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, zIndex: 9999 }} />
      )}
      {submitStatus === 'success' && (
        <Warning messageType="success" messageHeader={text.updateSuccess} />
      )}
      {submitStatus === 'error' && <Warning messageType="error" messageHeader={text.updateError} />}
      {status === 'loading' ? (
        <Spinner relCenter />
      ) : (
        <>
          <div className="notifications-modal-title">{text.yesnotify}</div>
          <div className="notifications-modal-entry">
            <Flex alignItems="center">
              <div className="text-14">{text.all}</div>
            </Flex>
            <div>
              <Checkbox
                label=""
                id="all"
                name="all"
                value={all}
                onChange={all ? () => null : setAll}
              />
            </div>
          </div>
          {data.map((rule) => {
            const rl = rules[rule.ruleID];
            if (!rl) return null;

            if (rule.options.length === 1) {
              return (
                <div className="notifications-modal-entry" key={rule.ruleID}>
                  <Flex alignItems="center">
                    <div className="text-14">{rl.title}</div>
                  </Flex>
                  <div>
                    <Checkbox
                      label=""
                      id={rule.ruleID.toString()}
                      name={rule.ruleID.toString()}
                      value={rule.active}
                      onChange={() => toggleRule(rule.ruleID)}
                    />
                  </div>
                </div>
              );
            }

            const selectedOption = rule.options.find((opt) => opt.active);
            return (
              <div className="notifications-modal-entry" key={rule.ruleID}>
                <Flex alignItems="center">
                  <div className="text-14">{rl.title}</div>
                  <select
                    value={selectedOption ? selectedOption.optionID : 'def'}
                    key={rule.ruleID + '-' + rule.active}
                    style={{ marginLeft: 10 }}
                    onChange={(e) => setOption(rule.ruleID, e.target.value)}
                  >
                    <option value="def">{text.chooserule}</option>
                    {rule.options.map((option, index) => (
                      <option key={option.optionID} value={option.optionID}>
                        {rl.opts[index]}
                      </option>
                    ))}
                  </select>
                  {rl.secondTitle && (
                    <div className="text-14" style={{ marginLeft: 10 }}>
                      {rl.secondTitle}
                    </div>
                  )}
                </Flex>
                <div>
                  <Checkbox
                    label=""
                    id={rule.ruleID.toString()}
                    name={rule.ruleID.toString()}
                    value={rule.active}
                    onChange={toggleRule}
                  />
                </div>
              </div>
            );
          })}
        </>
      )}
    </Modal>
  );
};

const initialState = {
  data: [],
  initial: [],
  status: 'loading',
  submitStatus: 'initial',
};

const reducer = produce((draft, action) => {
  switch (action.type) {
    case 'SET_OPTION':
      draft.submitStatus = 'initial';
      const { rule, option } = action.payload;
      const isDef = isNaN(option);
      draft.data.forEach((rl) => {
        if (rl.ruleID === rule) {
          rl.active = isDef ? false : true;
          rl.options.forEach((opt) => {
            if (opt.optionID === option) {
              opt.active = true;
            } else {
              opt.active = false;
            }
          });
        }
      });
      break;
    case 'TOGGLE_RULE':
      draft.submitStatus = 'initial';
      draft.data.forEach((rule) => {
        if (rule.ruleID === action.payload) {
          if (rule.active) {
            rule.active = false;
            rule.options.forEach((opt) => {
              opt.active = false;
            });
          } else {
            rule.active = true;
            const hasOneActive = rule.options.some((opt) => opt.active);
            if (!hasOneActive) {
              rule.options[0].active = true;
            }
          }
        }
      });
      break;
    case 'SET_ALL':
      draft.submitStatus = 'initial';
      draft.data.forEach((rule) => {
        rule.active = false;
        rule.options.forEach((opt) => {
          opt.active = false;
        });
      });
      break;
    case 'SET_LOADING':
      draft.status = 'loading';
      break;
    case 'SUBMIT':
      draft.submitStatus = 'loading';
      break;
    case 'SUBMIT_ERROR':
      draft.submitStatus = 'error';
      break;
    case 'SUBMIT_SUCCESS':
      draft.initial = draft.data;
      draft.submitStatus = 'success';
      break;
    case 'SET_DATA':
      draft.data = action.payload;
      draft.initial = action.payload;
      draft.status = 'loaded';
      break;
    default:
      return draft;
  }
});

const locale = {
  is: {
    close: 'Loka',
    updateSuccess: 'Stillingar hafa verið uppfærðar',
    updateError: 'Ekki tókst að uppfæra stillingar. Vinsamlegast reyndu aftur!',
    chooserule: 'Veldu reglu',
    yesnotify: 'Já, fá tilkynningu',
    all: 'Allar tilkynningar',
    title: 'Tölvupóstsendingar',
    rules: {
      39: {
        title: 'Lánshæfismat lögaðila fer upp í eða yfir',
        opts: [
          'lánshæfisflokk 1',
          'lánshæfisflokk 2',
          'lánshæfisflokk 3',
          'lánshæfisflokk 4',
          'lánshæfisflokk 5',
          'lánshæfisflokk 6',
          'lánshæfisflokk 7',
          'lánshæfisflokk 8',
          'lánshæfisflokk 9',
          'lánshæfisflokk 10',
        ],
      },
      40: {
        title: 'Lánshæfismat einstaklinga fer upp í eða yfir:',
        opts: [
          'lánshæfisflokk A1',
          'lánshæfisflokk A2',
          'lánshæfisflokk A3',
          'lánshæfisflokk B1',
          'lánshæfisflokk B2',
          'lánshæfisflokk B3',
          'lánshæfisflokk C1',
          'lánshæfisflokk C2',
          'lánshæfisflokk C3',
          'lánshæfisflokk D1',
          'lánshæfisflokk D2',
          'lánshæfisflokk D3',
        ],
      },
      41: {
        title: 'Fyrirtæki hoppar upp um',
        secondTitle: 'eða meira',
        opts: [
          '1 lánshæfisflokk',
          '2 lánshæfisflokka',
          '3 lánshæfisflokka',
          '4 lánshæfisflokka',
          '5 lánshæfisflokka',
        ],
      },
      42: {
        title: 'Fyrirtæki hrapar niður um',
        secondTitle: 'eða meira',
        opts: [
          '1 lánshæfisflokk',
          '2 lánshæfisflokka',
          '3 lánshæfisflokka',
          '4 lánshæfisflokka',
          '5 lánshæfisflokka',
        ],
      },
      43: {
        title: 'Einstaklingur hoppar upp um',
        secondTitle: 'eða meira',
        opts: [
          '1 lánshæfisflokk',
          '2 lánshæfisflokka',
          '3 lánshæfisflokka',
          '4 lánshæfisflokka',
          '5 lánshæfisflokka',
          '6 lánshæfisflokka',
          '7 lánshæfisflokka',
        ],
      },
      44: {
        title: 'Einstaklingur hrapar niður um',
        secondTitle: 'eða meira',
        opts: [
          '1 lánshæfisflokk',
          '2 lánshæfisflokka',
          '3 lánshæfisflokka',
          '4 lánshæfisflokka',
          '5 lánshæfisflokka',
          '6 lánshæfisflokka',
          '7 lánshæfisflokka',
        ],
      },
      45: {
        title: 'Fyrsta skráning á vanskilaskrá',
      },
      46: {
        title: 'Síðasta skráning á vanskilaskrá',
      },
      47: {
        title: 'Brottfelld félög og látnir einstaklinga',
      },
      48: {
        title: 'Gjaldþrot og árangurslaus fjárnám',
      },
      49: {
        title: 'Skráningar á válista RSK',
      },
    },
  },
  en: {
    close: 'Close',
    updateSuccess: 'Email notification settings updated',
    updateError: 'Unable to update notification settings. Please try again!',
    chooserule: 'Choose rule',
    yesnotify: 'Yes, notify me',
    all: 'All events',
    title: 'Email notifications',
    rules: {
      39: {
        title: "Company's credit score goes up to or above",
        opts: [
          'credit rating category 1',
          'credit rating category 2',
          'credit rating category 3',
          'credit rating category 4',
          'credit rating category 5',
          'credit rating category 6',
          'credit rating category 7',
          'credit rating category 8',
          'credit rating category 9',
          'credit rating category 10',
        ],
      },
      40: {
        title: "Individual's credit score goes up to or above",
        opts: [
          'credit rating category A1',
          'credit rating category A2',
          'credit rating category A3',
          'credit rating category B1',
          'credit rating category B2',
          'credit rating category B3',
          'credit rating category C1',
          'credit rating category C2',
          'credit rating category C3',
          'credit rating category D1',
          'credit rating category D2',
          'credit rating category D3',
        ],
      },
      41: {
        title: 'Company jumps up',
        secondTitle: 'or more',
        opts: [
          '1 credit rating category',
          '2 credit rating categories',
          '3 credit rating categories',
          '4 credit rating categories',
          '5 credit rating categories',
        ],
      },
      42: {
        title: 'Company falls down',
        secondTitle: 'or more',
        opts: [
          '1 credit rating category',
          '2 credit rating categories',
          '3 credit rating categories',
          '4 credit rating categories',
          '5 credit rating categories',
        ],
      },
      43: {
        title: 'Individual jumps up',
        secondTitle: 'or more',
        opts: [
          '1 credit rating category',
          '2 credit rating categories',
          '3 credit rating categories',
          '4 credit rating categories',
          '5 credit rating categories',
          '6 credit rating categories',
          '7 credit rating categories',
        ],
      },
      44: {
        title: 'Individual falls down',
        secondTitle: 'or more',
        opts: [
          '1 credit rating category',
          '2 credit rating categories',
          '3 credit rating categories',
          '4 credit rating categories',
          '5 credit rating categories',
          '6 credit rating categories',
          '7 credit rating categories',
        ],
      },
      45: {
        title: 'First registration to the default registry',
      },
      46: {
        title: 'Last registration to the default registry',
      },
      47: {
        title: 'Dissolved companies and deceased individuals',
      },
      48: {
        title: 'Bankruptcy and unsuccessful capitalization',
      },
      49: {
        title: 'Default registration with the internal revenue service (RSK)',
      },
    },
  },
};
